/**
 * Pure helpers voor GEDCOM NAME-block export en _PATR import (Refs #781).
 *
 * Geëxtraheerd uit gedcom.ts en GedcomImport.tsx zodat de logica
 * unit-testbaar is zonder de async API-keten van exportGedcom().
 *
 * Round-trip strategie:
 *  - Export: patronym wordt opgenomen in de NAME-string ("1 NAME Jan Pietersz /Kalden/")
 *    voor maximum interop met andere genealogie-tools (Aldfaer, Gramps, RootsMagic),
 *    plus expliciete "2 _PATR Pietersz" subtag voor onze eigen round-trip.
 *  - Import: "2 _PATR" onder NAME wordt herkend en aan person.patronym toegekend.
 *    De NAME-string-parsing kan patronym niet betrouwbaar van een 2e voornaam
 *    onderscheiden — daarom is _PATR de canonical bron voor de round-trip.
 *
 * GEDCOM 5.5.1 ondersteunt geen native PATR-tag; custom extensies krijgen een
 * underscore-prefix (_PATR). Tools zonder _PATR-support tolereren het als
 * onbekende tag (de NAME-string blijft compleet).
 */

export interface GedcomNamePerson {
  givenNames?: string | null;
  patronym?: string | null;
  surname?: string | null;
  surnamePrefix?: string | null;
  prefix?: string | null; // NPFX (Dr., Sir., etc.)
  suffix?: string | null; // NSFX (Jr., III, etc.)
  nickname?: string | null;
}

/**
 * Bouwt het complete NAME-blok (1 NAME + alle 2-level subtags) voor één persoon.
 * Geeft een string terug met newline-separators, klaar voor concatenatie in de
 * GEDCOM output.
 *
 * Voorbeeld:
 *   buildGedcomNameBlock({ givenNames: 'Jan', patronym: 'Pietersz', surname: 'Kalden' })
 *   // → "1 NAME Jan Pietersz /Kalden/\n2 GIVN Jan\n2 SURN Kalden\n2 _PATR Pietersz\n"
 */
export function buildGedcomNameBlock(p: GedcomNamePerson): string {
  const givenNames = (p.givenNames || '').trim();
  const patronym = (p.patronym || '').trim();
  const surname = (p.surname || '').trim();
  const surnamePrefix = (p.surnamePrefix || '').trim();

  // NAME-line: [givenNames] [patronym] /[surnamePrefix ]surname/
  const nameSegments: string[] = [];
  if (givenNames) nameSegments.push(givenNames);
  if (patronym) nameSegments.push(patronym);
  const beforeSlashes = nameSegments.join(' ');
  const surnameInSlashes = `${surnamePrefix ? surnamePrefix + ' ' : ''}${surname}`;

  let block = '';
  // Spatie tussen givenNames-deel en /surname/ alleen als beforeSlashes niet leeg is.
  block += `1 NAME ${beforeSlashes ? beforeSlashes + ' ' : ''}/${surnameInSlashes}/\n`;

  if (givenNames) block += `2 GIVN ${givenNames}\n`;
  if (surnamePrefix) block += `2 SPFX ${surnamePrefix}\n`;
  if (surname) block += `2 SURN ${surname}\n`;
  if (p.prefix) block += `2 NPFX ${p.prefix}\n`;
  if (p.suffix) block += `2 NSFX ${p.suffix}\n`;
  if (p.nickname) block += `2 NICK ${p.nickname}\n`;
  if (patronym) block += `2 _PATR ${patronym}\n`;

  return block;
}

/**
 * Parser-helper: gegeven een level-2 subtag onder NAME, retourneert de patronym-waarde
 * indien de subtag _PATR is en de waarde niet leeg, anders null.
 *
 * Bedoeld om ingehaakt te worden in de bestaande NAME-subtag-loop in GedcomImport.tsx.
 */
export function parseGedcomPatronym(subtag: string, value: string): string | null {
  if (subtag !== '_PATR') return null;
  const trimmed = (value || '').trim();
  return trimmed.length > 0 ? trimmed : null;
}
